"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { apiFetch, apiUrl } from "@/lib/api";
import {
  serviceReadiness,
  useSettings,
  type ServiceReadiness,
  type ServiceName,
} from "@/components/settings/SettingsContext";
import { SettingsLoadStatusBanner } from "@/components/settings/SettingsLoadStatusBanner";
import SettingsStatusPanel from "@/components/settings/SettingsStatusPanel";
import {
  SETTINGS_CATEGORIES,
  type Lang,
  type SettingsCategory,
} from "@/lib/settings-nav";
import {
  SYSTEM_SETTINGS_ZONES,
  categoriesForSystemZone,
  settingsHubState,
  type SystemSettingsZone,
} from "./settings-system-nav";

type NetworkPreview = {
  apiBase: string;
};

type ProviderCard = {
  href: string;
  service: ServiceName;
  titleKey: string;
  descriptionKey: string;
};

const MODEL_PROVIDER_CARDS: readonly ProviderCard[] = [
  {
    href: "/settings/llm",
    service: "llm",
    titleKey: "LLM provider",
    descriptionKey: "Choose available engines and the default engine used by personas.",
  },
  {
    href: "/settings/embedding",
    service: "embedding",
    titleKey: "Embedding",
    descriptionKey: "Embedding model and dimensions used for knowledge-map indexing",
  },
  {
    href: "/settings/stt",
    service: "stt",
    titleKey: "Speech STT",
    descriptionKey: "Voice-input transcription engine",
  },
  {
    href: "/settings/tts",
    service: "tts",
    titleKey: "Speech TTS",
    descriptionKey: "Read-aloud voice",
  },
  {
    href: "/settings/image",
    service: "imagegen",
    titleKey: "Image generation",
    descriptionKey: "Concept-diagram generation provider",
  },
  {
    href: "/settings/video",
    service: "videogen",
    titleKey: "Video generation",
    descriptionKey: "Concept-animation generation provider",
  },
];

export default function SettingsHub() {
  const { i18n, t } = useTranslation();
  const ko = i18n.language?.toLowerCase().startsWith("ko");
  const tr = useCallback((l: Lang) => (ko ? l.ko : l.en), [ko]);
  const [selectedZoneKey, setSelectedZoneKey] =
    useState<SystemSettingsZone["key"]>("models");
  const {
    catalog,
    catalogEditable,
    diagnosticsResults,
    settingsError,
    settingsLoading,
  } = useSettings();

  const selectedZone =
    SYSTEM_SETTINGS_ZONES.find((zone) => zone.key === selectedZoneKey) ??
    SYSTEM_SETTINGS_ZONES[0];
  const zoneCategories = useMemo(
    () => categoriesForSystemZone(selectedZone, SETTINGS_CATEGORIES),
    [selectedZone],
  );
  const state = settingsHubState({
    settingsLoading,
    settingsError,
    catalogEditable,
  });

  const modelStats = useMemo(() => {
    const category = SETTINGS_CATEGORIES.find((item) => item.key === "models");
    const services = (category?.children ?? []).filter((leaf) => leaf.service);
    if (catalogEditable !== true) {
      return {
        total: services.length,
        configured: -1,
        passed: 0,
        failed: 0,
        states: [] as ServiceReadiness[],
      };
    }
    const states = services.map((leaf) =>
      serviceReadiness(catalog, leaf.service!, diagnosticsResults),
    );
    return {
      total: services.length,
      configured: states.filter((item) => item !== "not_configured").length,
      passed: states.filter((item) => item === "passed").length,
      failed: states.filter((item) => item === "failed").length,
      states,
    };
  }, [catalog, catalogEditable, diagnosticsResults]);
  const providerConfigured = useMemo(
    () =>
      MODEL_PROVIDER_CARDS.map(
        (card) =>
          serviceReadiness(catalog, card.service, diagnosticsResults) !==
          "not_configured",
      ),
    [catalog, diagnosticsResults],
  );

  const [network, setNetwork] = useState<NetworkPreview | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await apiFetch(apiUrl("/api/v1/settings/network"));
        if (!response.ok) return;
        const data = (await response.json()) as {
          effective?: { browser_api_base?: string };
        };
        if (!cancelled) {
          setNetwork({ apiBase: data.effective?.browser_api_base || "" });
        }
      } catch {
        // A 403 or unreachable backend leaves the existing category blurb intact.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-state={state}
      className="settings-system-shell flex h-full min-w-0 bg-[var(--background)]"
    >
      <aside className="settings-system-nav flex w-[216px] shrink-0 flex-col border-r border-[var(--border)] bg-[var(--card)] px-[14px] py-4">
        <div className="mb-[10px] px-[2px] text-[13px] font-bold leading-[17.05px] text-[var(--muted-foreground)]">
          {t("Advanced · System")}
        </div>
        <nav aria-label={t("Advanced · System")} className="space-y-0.5">
          {SYSTEM_SETTINGS_ZONES.map((zone) => {
            const Icon = zone.icon;
            const selected = zone.key === selectedZone.key;
            return (
              <button
                key={zone.key}
                type="button"
                data-tour={`tour-zone-${zone.key}`}
                aria-current={selected ? "page" : undefined}
                onClick={() => setSelectedZoneKey(zone.key)}
                className={`settings-system-nav__item flex h-[42px] w-full items-center gap-[9px] rounded-[11px] px-[11px] text-left transition-colors ${
                  selected
                    ? "bg-[var(--secondary)] text-[var(--primary)]"
                    : "text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                }`}
              >
                <Icon size={16} strokeWidth={1.8} aria-hidden />
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">
                  {t(zone.labelKey)}
                </span>
                <span className="text-[13px] font-bold tabular-nums">
                  {zone.itemCount}
                </span>
              </button>
            );
          })}
        </nav>
        <p className="settings-system-nav__hint mt-[14px] min-h-[79px] rounded-[11px] border border-[var(--border)] bg-[var(--background)] p-[11px] text-[13px] leading-[18px] text-[var(--muted-foreground)]">
          {t(
            "This zone is separate from learning because it configures the system and follows a different learning-flow hierarchy.",
          )}
        </p>
      </aside>

      <section className="settings-system-content min-w-0 flex-1 overflow-y-auto px-7 pt-[22px] pb-[60px]">
        <header className="mb-4 flex min-h-[27.89px] flex-wrap items-baseline gap-x-[10px] gap-y-1">
          <h2 className="text-[18px] font-extrabold leading-[27.9px] text-[var(--foreground)]">
            <span>{t(selectedZone.labelKey)}</span>
            {selectedZone.key === "models" && (
              <>
                <span aria-hidden> </span>
                <span>{t("Models")}</span>
              </>
            )}
          </h2>
          {selectedZone.key === "models" && (
            <p className="text-[13.5px] leading-[19.375px] text-[var(--muted-foreground)]">
              {t("Configure LLM, embedding, STT, TTS, image, and video providers")}
            </p>
          )}
        </header>

        {state === "loading" ? (
          <LoadingProviderGrid />
        ) : state === "error" ? (
          <SettingsLoadStatusBanner />
        ) : selectedZone.key === "models" && state === "forbidden" ? (
          <div
            role="alert"
            className="rounded-[14px] border border-dashed border-[var(--border)] px-[15px] py-[14px] text-[13.5px] leading-5 text-[var(--muted-foreground)]"
          >
            {t(
              "Model endpoints are assigned by your administrator. You can still personalize theme and language here.",
            )}
          </div>
        ) : selectedZone.key === "models" ? (
          <>
            <ProviderGrid configured={providerConfigured} />
            <Link
              href="/settings/models"
              data-tour="tour-cat-models"
              className="mt-3 inline-flex items-center gap-1 text-[13.5px] font-semibold text-[var(--primary)] hover:underline"
            >
              {t("All model settings")}
              <ChevronRight size={14} aria-hidden />
            </Link>
            {modelStats.configured >= 0 && (
              <div className="mt-2">
                <ModelPreview stats={modelStats} tr={tr} />
              </div>
            )}
          </>
        ) : (
          <ZoneCategoryGrid
            categories={zoneCategories}
            shortcut={selectedZone.shortcut}
            tr={tr}
            network={network}
            t={t}
          />
        )}

        {!settingsLoading && !settingsError && (
          <div className="mt-5">
            <SettingsStatusPanel />
          </div>
        )}
      </section>
    </div>
  );
}

function LoadingProviderGrid() {
  return (
    <div className="settings-provider-grid grid grid-cols-[repeat(auto-fill,minmax(268px,1fr))] gap-3">
      {MODEL_PROVIDER_CARDS.map((card) => (
        <div
          key={card.href}
          aria-busy="true"
          className="settings-load-skeleton min-h-[79.27px] animate-pulse rounded-[14px] border border-[var(--border)] bg-[var(--card)] p-[14px_15px]"
        >
          <div className="h-[18px] w-2/5 rounded bg-[var(--muted)]" />
          <div className="mt-[9px] h-3 w-4/5 rounded bg-[var(--muted)]" />
        </div>
      ))}
    </div>
  );
}

function ProviderGrid({ configured }: { configured: readonly boolean[] }) {
  const { t } = useTranslation();

  return (
    <div className="settings-provider-grid grid grid-cols-[repeat(auto-fill,minmax(268px,1fr))] gap-3">
      {MODEL_PROVIDER_CARDS.map((card, index) => (
        <Link
          key={card.href}
          href={card.href}
          className="settings-provider-card min-h-[79.27px] rounded-[14px] border border-[var(--border)] bg-[var(--card)] p-[14px_15px] shadow-[var(--shadow-soft)] transition-colors hover:border-[var(--ring)]"
        >
          <div className="settings-provider-card__head flex min-h-[22.27px] items-center">
            <h3 className="settings-provider-card__title text-[14px] font-bold leading-[21.7px] text-[var(--foreground)]">
              {t(card.titleKey)}
            </h3>
            <span className="settings-provider-card__badge ml-auto rounded-full bg-[var(--muted)] px-2 py-[3px] text-[13px] font-bold leading-[16.27px] text-[var(--muted-foreground)]">
              {t(configured[index] ? "Configured" : "Not set")}
            </span>
          </div>
          <p className="settings-provider-card__description mt-[7px] text-[13.5px] leading-5 text-[var(--muted-foreground)]">
            {t(card.descriptionKey)}
          </p>
        </Link>
      ))}
    </div>
  );
}

function ZoneCategoryGrid({
  categories,
  shortcut,
  tr,
  network,
  t,
}: {
  categories: SettingsCategory[];
  shortcut?: { href: string; labelKey: string };
  tr: (lang: Lang) => string;
  network: NetworkPreview | null;
  t: (key: string) => string;
}) {
  return (
    <div className="settings-provider-grid grid grid-cols-[repeat(auto-fill,minmax(268px,1fr))] gap-3">
      {categories.map((category) => (
        <CategoryCard
          key={category.key}
          category={category}
          tr={tr}
          network={category.key === "network" ? network : null}
          t={t}
        />
      ))}
      {shortcut && <ShortcutCard shortcut={shortcut} t={t} />}
    </div>
  );
}

function CategoryCard({
  category,
  tr,
  network,
  t,
}: {
  category: SettingsCategory;
  tr: (lang: Lang) => string;
  network: NetworkPreview | null;
  t: (key: string) => string;
}) {
  const Icon: LucideIcon = category.icon;
  return (
    <Link
      href={category.href}
      data-tour={`tour-cat-${category.key}`}
      className="settings-provider-card group min-h-[79.27px] rounded-[14px] border border-[var(--border)] bg-[var(--card)] p-[14px_15px] shadow-[var(--shadow-soft)] transition-colors hover:border-[var(--ring)]"
    >
      <div className="settings-provider-card__head flex min-h-[22.27px] items-center gap-2">
        <Icon
          size={16}
          strokeWidth={1.7}
          className="shrink-0 text-[var(--muted-foreground)]"
        />
        <h3 className="settings-provider-card__title min-w-0 truncate text-[14px] font-bold leading-[21.7px] text-[var(--foreground)]">
          {tr(category.label)}
        </h3>
        <ChevronRight
          size={14}
          className="ml-auto shrink-0 text-[var(--muted-foreground)]"
          aria-hidden
        />
      </div>
      {network ? (
        <NetworkPreviewRow network={network} t={t} />
      ) : (
        <p className="settings-provider-card__description mt-[7px] truncate text-[13.5px] leading-5 text-[var(--muted-foreground)]">
          {tr(category.blurb)}
        </p>
      )}
    </Link>
  );
}

function ShortcutCard({
  shortcut,
  t,
}: {
  shortcut: { href: string; labelKey: string };
  t: (key: string) => string;
}) {
  return (
    <Link
      href={shortcut.href}
      className="settings-provider-card group min-h-[79.27px] rounded-[14px] border border-[var(--border)] bg-[var(--card)] p-[14px_15px] shadow-[var(--shadow-soft)] transition-colors hover:border-[var(--ring)]"
    >
      <div className="settings-provider-card__head flex min-h-[22.27px] items-center gap-2">
        <h3 className="settings-provider-card__title text-[14px] font-bold leading-[21.7px] text-[var(--foreground)]">
          {t(shortcut.labelKey)}
        </h3>
        <ChevronRight
          size={14}
          className="ml-auto shrink-0 text-[var(--muted-foreground)]"
          aria-hidden
        />
      </div>
    </Link>
  );
}

function ModelPreview({
  stats,
  tr,
}: {
  stats: {
    total: number;
    configured: number;
    passed: number;
    failed: number;
    states: ServiceReadiness[];
  };
  tr: (lang: Lang) => string;
}) {
  return (
    <div className="flex items-center gap-2.5 text-[13.5px] text-[var(--muted-foreground)]">
      <div className="flex items-center gap-1">
        {stats.states.map((item, index) => (
          <span
            key={index}
            className={`h-1.5 w-1.5 rounded-full ${
              item === "passed"
                ? "bg-emerald-500"
                : item === "failed"
                  ? "bg-red-500"
                  : item === "untested"
                    ? "bg-zinc-400 dark:bg-zinc-500"
                    : "bg-zinc-300/60 dark:bg-zinc-700"
            }`}
          />
        ))}
      </div>
      <span className="tabular-nums">
        {stats.failed > 0
          ? tr({
              en: `${stats.configured}/${stats.total} configured · ${stats.failed} failed`,
              ko: `${stats.configured}/${stats.total}개 설정됨 · ${stats.failed}개 실패`,
            })
          : stats.passed > 0
            ? tr({
                en: `${stats.configured}/${stats.total} configured · ${stats.passed} passed`,
                ko: `${stats.configured}/${stats.total}개 설정됨 · ${stats.passed}개 통과`,
              })
            : tr({
                en: `${stats.configured}/${stats.total} configured`,
                ko: `${stats.configured}/${stats.total}개 설정됨`,
              })}
      </span>
    </div>
  );
}

function NetworkPreviewRow({
  network,
  t,
}: {
  network: NetworkPreview;
  t: (key: string) => string;
}) {
  return (
    <div className="mt-[7px] flex items-center gap-2 text-[13.5px] leading-5 text-[var(--muted-foreground)]">
      <span className="shrink-0 text-[var(--muted-foreground)]/70">
        {t("API")}
      </span>
      <span
        className="truncate font-mono text-[13.5px] text-[var(--foreground)]"
        title={network.apiBase}
      >
        {network.apiBase || t("local")}
      </span>
    </div>
  );
}